"use client";

import { MusicalNoteIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Tooltip } from "react-tooltip";

import { InvalidLengthMaxModal } from "@/components/fileCoverInput/inputMethods/InvalidLengthMaxModal";
import { InvalidLengthSubscriptionModal } from "@/components/InvalidLengthSubscriptionModal";
import {
  MAX_NUMBER_OF_CONVERSION_INPUTS,
  MINUTES_MAX_UPLOAD_CONVERSION,
} from "@/constants/constants";
import {
  conversionNamespace,
  getConversionInputNamespace,
} from "@/constants/namespaces";
import { useAudioInput } from "@/lib/zustand/useAudioInput";
import { useNamedPitchCorrection } from "@/lib/zustand/usePitchCorrection";
import {
  AudioInputState,
  IsolationConstructedNoDates,
  UserNoDates,
} from "@/types/types";
import { parseDatesSound } from "@/utils/serverClientSerialization/parseDatesSound";
import { isValidLengthSubscription } from "@/utils/subscriptionLevels/time/isValidLengthSubscription";

export function ApplyIsolationForConversion({
  isolation,
  user,
}: {
  isolation: IsolationConstructedNoDates;
  user: UserNoDates;
}) {
  // Set up AudioInputState stuff ////////////////////////////////
  const clearInputs: AudioInputState["clearInput"][] = [];

  for (let i = 0; i < MAX_NUMBER_OF_CONVERSION_INPUTS; i++) {
    const useConversionInputStore = useAudioInput((state) =>
      state.getNamedStore(getConversionInputNamespace(i))
    );

    const clearInput = useConversionInputStore((state) => state.clearInput);

    clearInputs.push(clearInput);
  }

  const useConversionInput = useAudioInput((state) =>
    state.getNamedStore(getConversionInputNamespace(0))
  );

  const setInputSound = useConversionInput((state) => state.setInputSound);
  //////////////////////////////////////////////////////////////////////////////

  // Set up pitch correction stuff ////////////////////////////////////////////
  const usePitchCorrection = useNamedPitchCorrection((state) =>
    state.getNamedStore(conversionNamespace)
  );

  const unset = usePitchCorrection((state) => state.unset);
  //////////////////////////////////////////////////////////////////////////////

  const [
    invalidLengthSubscriptionModalOpen,
    setInvalidLengthSubscriptionModalOpen,
  ] = useState(false);
  const [invalidLengthMaxModalOpen, setInvalidLengthMaxModalOpen] =
    useState(false);

  const router = useRouter();

  const clickHandler = () => {
    // Check if the audio length is within the limits Subscription check
    const audioLength = isolation.outputSound!.lengthS;
    const validSubscription = isValidLengthSubscription(
      user.conversionTimeLeftSeconds,
      audioLength
    );

    if (!validSubscription) {
      setInvalidLengthSubscriptionModalOpen(true);
      return;
    }

    // Application limit check
    const validMax = audioLength <= MINUTES_MAX_UPLOAD_CONVERSION * 60;

    if (!validMax) {
      setInvalidLengthMaxModalOpen(true);
      return;
    }

    // Clear all inputs
    clearInputs.forEach((clearInput) => clearInput());
    unset();
    // Set the sound to the input
    setInputSound(parseDatesSound(isolation.outputSound!));
    router.push("/create");
  };

  return (
    <>
      <InvalidLengthSubscriptionModal
        open={invalidLengthSubscriptionModalOpen}
        closeModal={() => setInvalidLengthSubscriptionModalOpen(false)}
        user={user}
      />

      <InvalidLengthMaxModal
        open={invalidLengthMaxModalOpen}
        closeModal={() => setInvalidLengthMaxModalOpen(false)}
        audioLengthMinLimitMinutes={null}
        audioLengthMaxLimitMinutes={MINUTES_MAX_UPLOAD_CONVERSION}
      />
      <button
        className={`isolation-id-use-${isolation.id}`}
        onClick={clickHandler}
      >
        <MusicalNoteIcon
          data-tooltip-id={`use-isolation-tooltip-${isolation.id}`}
          data-tooltip-content="Use in conversion"
          data-tooltip-place="top"
          className="h-6 w-6 stroke-gray-900"
        />
        <Tooltip id={`use-isolation-tooltip-${isolation.id}`} />
      </button>
    </>
  );
}
